import {z} from 'zod';
import {CopilotExcelFileContent, ToolExecuteParams, ToolResult} from '../../copilot.types';
import toolResult from '../../utils/creators/toolResult';
import toolError from '../../utils/toolError';
import {CopilotFileContentType, ToolName} from '../../copilot.enums';
import {excelApi} from 'common/src/modules/excel/excel.api';
import analyzeWorksheetHeaders from '../../utils/analyzeWorksheetHeaders';
import getUniqueColumnValues from './utils/getUniqueColumnValues';
import tool from '../../utils/creators/tool';

const inputSchema = z.object({
  fileId: z.string(),
  worksheetIndex: z.number().describe('The index of the worksheet to read.'),
});

function readExcelWorksheetTool(config?: {
  readValues: boolean;
  readHeaders: boolean;
  excelDataRowsLimit: number;
}) {
  const {readValues = true, readHeaders = true, excelDataRowsLimit = 100} = config || {};

  async function execute({
    toolUtils,
    input: {fileId, worksheetIndex},
    callChain,
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const fileInfo = toolUtils.getFileById(fileId);

    if (!fileInfo) {
      return toolError(`File (ID: ${fileId}) not found`);
    }

    if (fileInfo.content?.type !== CopilotFileContentType.Excel) {
      return toolError(`File (ID: ${fileId}) is not an excel file`);
    }

    // get the first worksheet data rows
    const excelContent = fileInfo.content as CopilotExcelFileContent;
    const [worksheet] = await excelApi.readExcelFile({
      fileKey: fileInfo.fileKey,
      worksheetIndex,
      rowsLimit: excelDataRowsLimit + 15, // Get some extra rows to make sure that the last row is empty. Those extra rows should be enough to contain header rows and possible empty row at the beginning of the file.
    });

    const headers = await analyzeWorksheetHeaders(worksheet, toolUtils, callChain);
    const firstDataRowIndex = Math.max(...headers.map(({rowIndex}) => rowIndex)) + 1;

    const dataRows = worksheet.rows.slice(
      firstDataRowIndex,
      firstDataRowIndex + excelDataRowsLimit,
    );
    const excessiveDataRow = worksheet.rows.at(firstDataRowIndex + excelDataRowsLimit);

    if (!!excessiveDataRow) {
      return toolError(
        `The excel file has more than ${excelDataRowsLimit} rows. Please upload an excel with less than ${excelDataRowsLimit} rows.`,
      );
    }

    const lastHeaderRowIndex = Math.max(...headers.map(({rowIndex}) => rowIndex));
    const lastHeaderRow = worksheet.rows[lastHeaderRowIndex];

    // get the unique values for each column
    const uniqueColumnValues = readValues ? getUniqueColumnValues(dataRows) : undefined;

    // return the copilot file info, with the added information about the unique column values
    const result = {
      ...fileInfo,
      content: {
        ...excelContent,
        headerRowIndex: lastHeaderRowIndex,
        headerColumnIndex: headers.find(({rowIndex}) => rowIndex === lastHeaderRowIndex)
          ?.columnIndex,
        firstDataRowIndex,
        headers: readHeaders ? headers : undefined,
        uniqueColumnValues: uniqueColumnValues
          ? uniqueColumnValues.map((values, columnIndex) => ({
              columnIndex,
              columnHeader: lastHeaderRow[columnIndex],
              uniqueValues: values,
            }))
          : [],
      },
    };

    return toolResult(JSON.stringify(result));
  }

  return tool({
    name: ToolName.ReadExcelWorksheet,
    description: 'Reads the contents of an excel worksheet.',
    execute,
    displayName: 'Reading worksheet',
    inputSchema,
  });
}

export default readExcelWorksheetTool;
